/**
 * Google Identity Services token flow.
 *
 * GIS is loaded by a script tag in index.html, so the client may not exist on
 * first render. The hook waits for it rather than assuming.
 *
 * The token is kept in sessionStorage, so a page reload inside the same tab
 * does not force a reconnect. Tokens are short-lived and scoped to the sheet.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "../config";

/** Versioned: bumping it discards tokens issued for an older scope set. */
const STORAGE_KEY = "dashboard.token.v2";

interface StoredToken {
  value: string;
  expiresAt: number;
}

interface TokenClient {
  requestAccessToken: (overrides?: { prompt?: string }) => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (options: {
            client_id: string;
            scope: string;
            callback: (response: {
              access_token?: string;
              expires_in?: number;
              error?: string;
            }) => void;
          }) => TokenClient;
        };
      };
    };
  }
}

function load(): string | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as StoredToken;
    if (stored.expiresAt > Date.now() + 30_000) return stored.value;
  } catch {
    // Corrupt entry, fall through and clear it.
  }
  sessionStorage.removeItem(STORAGE_KEY);
  return null;
}

function save(value: string, expiresIn: number): void {
  const stored: StoredToken = {
    value,
    expiresAt: Date.now() + expiresIn * 1000,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

async function waitForGis(): Promise<void> {
  for (let attempt = 0; attempt < 60; attempt++) {
    if (window.google?.accounts?.oauth2) return;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  throw new Error("Google sign-in did not load. Check your connection and reload.");
}

export function useGoogleAuth() {
  const [token, setToken] = useState<string | null>(() => load());
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<TokenClient | null>(null);

  useEffect(() => {
    let cancelled = false;
    waitForGis()
      .then(() => {
        if (cancelled) return;
        clientRef.current = window.google!.accounts.oauth2.initTokenClient({
          client_id: config.clientId,
          scope: config.scope,
          callback: (response) => {
            if (response.error || !response.access_token) {
              setError("Google did not grant access. Try connecting again.");
              return;
            }
            save(response.access_token, response.expires_in ?? 3600);
            setError(null);
            setToken(response.access_token);
          },
        });
        setReady(true);
      })
      .catch((cause: Error) => !cancelled && setError(cause.message));
    return () => {
      cancelled = true;
    };
  }, []);

  const connect = useCallback(() => {
    setError(null);
    clientRef.current?.requestAccessToken();
  }, []);

  const disconnect = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken(null);
  }, []);

  /** Call when the API reports 401 so the next action re-prompts. */
  const invalidate = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setError("Your Google session expired. Connect again to continue.");
  }, []);

  return { token, ready, error, connect, disconnect, invalidate };
}
